import { useTranslation } from 'react-i18next';
import { DropdownMenuItem, Icon } from '@kognitos/lattice';

import type { V1CodeManifest } from '@/generated/hooks/grimoire/models';
import { manifestToMarkdown } from '@/modules/automation/utils/manifest-to-markdown';
import { downloadBlob } from '@/shared/utils';

interface ISopExportFormat {
  /** Stable id — also the `data-testid` suffix. */
  id: string;
  /** i18n key for this format's menu-item label. */
  labelKey: string;
  /** File extension, without the dot. */
  extension: string;
  mimeType: string;
  serialize: (markdown: string) => BlobPart;
}

/** The SOP export formats offered in the picker.
 *
 *  Markdown is the only format FOR NOW. PDF and DOCX are both wanted and were
 *  deliberately deferred — not overlooked — for one reason: each needs a new
 *  dependency, and that is a call for a human to make.
 *
 *  Nothing already installed can write either format, so don't re-do this
 *  search: `exceljs` writes xlsx only (and every use in this repo is a read);
 *  `react-pdf` and `docx-preview` render existing files and cannot produce
 *  them; `jszip`/`fflate` are hoisted transitives of those two, so importing
 *  one would be an undeclared-dependency violation that breaks on any bump.
 *
 *  Three routes, whenever someone picks this up:
 *    1. Add a writer — `docx` for DOCX, `jspdf` or `pdf-lib` for PDF.
 *    2. Browser print-to-PDF — no dependency, but it opens the print dialog
 *       instead of downloading a file, so it is a UX decision too.
 *    3. Generate server-side and download the result.
 *
 *  Adding a format is then a data change, not a UI one: append an entry here
 *  with its `serialize`, and the menu picks it up. Once there is more than one
 *  format, this flat list is the point to reconsider a nested submenu — and to
 *  shorten each label back to the bare format, since "Export as" would then be
 *  repeated down the list and belongs on a parent instead.
 *
 *  Formats we can't yet produce stay absent rather than rendered disabled — a
 *  greyed-out "PDF" is an apology in the middle of a demo. */
const SOP_EXPORT_FORMATS: readonly ISopExportFormat[] = [
  {
    id: 'markdown',
    labelKey: 'automation.manifestSopView.export.markdown',
    extension: 'md',
    mimeType: 'text/markdown;charset=utf-8',
    serialize: markdown => markdown,
  },
];

/** Turns an SOP title into a safe download filename stem: whitespace and
 *  anything a filesystem or a `Content-Disposition` header would object to
 *  collapses to a hyphen. */
export function toExportFilenameStem(title: string, fallback: string): string {
  const stem = title
    .replace(/[<>:"/\\|?*\s]+/g, '-')
    // Leading dots hide the file on POSIX and trailing dots are invalid on
    // Windows; same treatment for the stray hyphens the collapse above leaves
    // at either end.
    .replace(/^[.-]+|[.-]+$/g, '')
    .slice(0, 80)
    .replace(/[.-]+$/, '');
  return stem || fallback;
}

interface IManifestSopExportMenuItemsProps {
  manifest: V1CodeManifest;
  /** Used for both the document heading and the filename when the manifest
   *  carries no title of its own (e.g. the automation name). */
  titleFallback?: string;
  /** Whether to render a leading icon. Follow the host menu: the page header's
   *  overflow menu labels every item with one, the title menu labels none. */
  withIcon?: boolean;
}

/** The SOP export entries, as bare `DropdownMenuItem`s for embedding in a
 *  caller's menu — they live in the page header's overflow menu rather than
 *  owning a trigger of their own.
 *
 *  No section label above them: with one format that would be a heading over a
 *  single item, and the item names the action itself ("Export as Markdown
 *  (.md)"). Add the label back alongside a submenu if a second format lands.
 *
 *  Serialises the typed `CodeManifest` (never the editor's markdown artifact),
 *  so the exported document is exactly what the step view shows. */
export function ManifestSopExportMenuItems({
  manifest,
  titleFallback,
  withIcon = true,
}: IManifestSopExportMenuItemsProps) {
  const { t: tAutomation } = useTranslation('automation');

  const handleExport = (format: ISopExportFormat) => {
    const markdown = manifestToMarkdown(
      manifest,
      {
        inputs: tAutomation('automation.manifestSopView.inputs'),
        outputs: tAutomation('automation.manifestSopView.outputs'),
        steps: tAutomation('automation.manifestSopView.steps'),
        untitledStep: tAutomation('automation.manifestSopView.step.untitled'),
        name: tAutomation('automation.manifestSopView.io.name'),
        type: tAutomation('automation.manifestSopView.io.type'),
        description: tAutomation('automation.manifestSopView.io.description'),
      },
      titleFallback,
    );
    const stem = toExportFilenameStem(
      manifest.title || titleFallback || '',
      tAutomation('automation.manifestSopView.export.fallbackFilename'),
    );
    downloadBlob(
      new Blob([format.serialize(markdown)], { type: format.mimeType }),
      `${stem}.${format.extension}`,
    );
  };

  return (
    <>
      {SOP_EXPORT_FORMATS.map(format => (
        <DropdownMenuItem
          key={format.id}
          data-testid={`sop-export-${format.id}`}
          onSelect={() => handleExport(format)}
        >
          {withIcon && <Icon type="FileText" size="sm" />}
          {tAutomation(format.labelKey)}
        </DropdownMenuItem>
      ))}
    </>
  );
}
